import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core';
import { DEFAULT_VOICE_CONFIG, VoiceConfig } from '../models';

/**
 * Service de capture audio via MediaRecorder
 * Gère l'enregistrement du microphone et retourne un Blob audio
 */
@Injectable({ providedIn: 'root' })
export class AudioCaptureService implements OnDestroy {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private animationFrameId: number | null = null;
  private recordingStartTime = 0;
  private durationInterval: ReturnType<typeof setInterval> | null = null;
  private silenceTimeout: ReturnType<typeof setTimeout> | null = null;

  private config: VoiceConfig = DEFAULT_VOICE_CONFIG;

  // Signals
  private _isRecording = signal(false);
  private _audioLevel = signal(0);
  private _duration = signal(0);
  private _hasPermission = signal<boolean | null>(null);
  private _error = signal<string | null>(null);

  // Public computed
  isRecording = computed(() => this._isRecording());
  audioLevel = computed(() => this._audioLevel());
  duration = computed(() => this._duration());
  hasPermission = computed(() => this._hasPermission());
  error = computed(() => this._error());

  /**
   * Vérifie si le navigateur supporte MediaRecorder
   */
  get isSupported(): boolean {
    return !!(navigator.mediaDevices && 'getUserMedia' in navigator.mediaDevices && window.MediaRecorder);
  }

  /**
   * Démarre l'enregistrement audio
   * @returns Promise<Blob> - Le blob audio une fois l'enregistrement terminé
   */
  async startRecording(): Promise<Blob> {
    if (this._isRecording()) {
      throw new Error('Enregistrement déjà en cours');
    }

    this._error.set(null);

    try {
      // Demander l'accès au microphone
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        }
      });

      this._hasPermission.set(true);

      // Configurer l'analyseur audio pour le niveau
      this.setupAudioAnalyser();

      // Créer le MediaRecorder
      const mimeType = this.getSupportedMimeType();
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
      this.audioChunks = [];

      return new Promise<Blob>((resolve, reject) => {
        if (!this.mediaRecorder) {
          reject(new Error('MediaRecorder non initialisé'));
          return;
        }

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          const audioBlob = new Blob(this.audioChunks, { type: mimeType });
          this.cleanup();
          resolve(audioBlob);
        };

        this.mediaRecorder.onerror = (event) => {
          this.cleanup();
          reject(new Error('Erreur MediaRecorder'));
        };

        // Démarrer l'enregistrement
        this.mediaRecorder.start(100); // Collecter toutes les 100ms

        // Attendre que le MediaRecorder soit vraiment prêt (warmup)
        // puis indiquer que l'enregistrement est actif
        setTimeout(() => {
          this._isRecording.set(true);
          this.recordingStartTime = Date.now();

          // Timer pour la durée
          this.startDurationTimer();

          // Auto-stop après durée max
          setTimeout(() => {
            if (this._isRecording()) {
              this.stopRecording();
            }
          }, this.config.maxRecordingDuration * 1000);

          // Démarrer la détection de silence
          if (this.config.autoStopOnSilence) {
            this.startSilenceDetection();
          }
        }, 300); // 300ms de warmup
      });
    } catch (err) {
      this._hasPermission.set(false);
      // Message du navigateur (anglais, technique) remplacé par une consigne (MOKA-363).
      const message = microphoneErrorMessage(err);
      this._error.set(message);
      throw new Error(message);
    }
  }

  /**
   * Arrête l'enregistrement en cours
   */
  stopRecording(): void {
    if (this.mediaRecorder && this._isRecording()) {
      this.mediaRecorder.stop();
      this._isRecording.set(false);
    }
  }

  /**
   * Annule l'enregistrement sans retourner de données
   */
  cancelRecording(): void {
    this.cleanup();
    this._isRecording.set(false);
  }

  /**
   * Configure le service avec des options personnalisées
   */
  configure(config: Partial<VoiceConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Détecte le format audio supporté
   */
  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'audio/webm';
  }

  /**
   * Configure l'analyseur audio pour mesurer le niveau
   */
  private setupAudioAnalyser(): void {
    if (!this.stream) return;

    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;

    const source = this.audioContext.createMediaStreamSource(this.stream);
    source.connect(this.analyser);

    this.updateAudioLevel();
  }

  /**
   * Met à jour le niveau audio en temps réel
   */
  private updateAudioLevel(): void {
    if (!this.analyser || !this._isRecording()) return;

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(dataArray);

    // Calculer le niveau moyen
    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    const normalizedLevel = Math.min(average / 128, 1);

    this._audioLevel.set(normalizedLevel);

    this.animationFrameId = requestAnimationFrame(() => this.updateAudioLevel());
  }

  /**
   * Timer pour afficher la durée d'enregistrement
   */
  private startDurationTimer(): void {
    this._duration.set(0);
    this.durationInterval = setInterval(() => {
      const elapsed = (Date.now() - this.recordingStartTime) / 1000;
      this._duration.set(Math.floor(elapsed));
    }, 100);
  }

  /**
   * Détection de silence pour auto-stop
   */
  private startSilenceDetection(): void {
    const checkSilence = () => {
      if (!this._isRecording()) return;

      if (this._audioLevel() < this.config.silenceThreshold) {
        // Silence détecté, démarrer le timeout
        if (!this.silenceTimeout) {
          this.silenceTimeout = setTimeout(() => {
            if (this._isRecording() && this._duration() > 1) {
              this.stopRecording();
            }
          }, this.config.silenceDuration);
        }
      } else {
        // Audio détecté, annuler le timeout
        if (this.silenceTimeout) {
          clearTimeout(this.silenceTimeout);
          this.silenceTimeout = null;
        }
      }

      if (this._isRecording()) {
        setTimeout(checkSilence, 100);
      }
    };

    // Attendre 1s avant de commencer la détection
    setTimeout(checkSilence, 1000);
  }

  /**
   * Nettoie les ressources
   */
  private cleanup(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }

    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
      this.silenceTimeout = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    this.analyser = null;
    this.mediaRecorder = null;
    this.audioChunks = [];
    this._audioLevel.set(0);
    this._duration.set(0);
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}

/**
 * Échec d'accès au micro traduit en consigne, avec le repli vers l'écrit (MOKA-363).
 * `NotAllowedError` : refus de la personne ou du navigateur ; `NotFoundError` : aucun micro ;
 * `NotReadableError` : micro pris par une autre application.
 */
export function microphoneErrorMessage(err: unknown): string {
  const name = (err as { name?: string } | null)?.name;
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Le micro est bloqué pour Mokah+. Autorisez-le dans les réglages du navigateur (icône à gauche de l\'adresse), ou écrivez votre demande ci-dessous.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'Aucun micro n\'a été trouvé sur cet appareil. Écrivez votre demande ci-dessous.';
    case 'NotReadableError':
    case 'AbortError':
      return 'Le micro est utilisé par une autre application. Fermez-la, ou écrivez votre demande ci-dessous.';
    default:
      return 'Le micro n\'a pas pu démarrer. Écrivez votre demande ci-dessous.';
  }
}
